import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { useFormStore, useNavigation } from '../store/formStore';
import { Edit2, User, MapPin, CreditCard } from 'lucide-react';

export function SummaryStep() {
  const { formData, submitForm, isSubmitting, updateField } = useFormStore();
  const { goToPreviousStep, goToStep, canGoBack } = useNavigation();
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(formData.hasAcceptedTerms ?? false);
  const [acceptYelloanOffers, setAcceptYelloanOffers] = useState(formData.acceptYelloanOffers ?? true);
  const [acceptPartnerOffers, setAcceptPartnerOffers] = useState(formData.acceptPartnerOffers ?? true);

  const projectTypeLabels: Record<string, string> = {
    'travaux': 'Travaux maison',
    'besoin': "Besoin d'argent",
    'auto': 'Auto / moto',
    'conso': 'Consommation'
  };
  
  const titleLabels: Record<string, string> = {
    'M': 'Monsieur',
    'MME': 'Madame',
    'MLE': 'Mademoiselle'
  };
  
  const countryLabels: Record<string, string> = {
    'FR': 'France',
    'MC': 'Monaco'
  };
  
  const handleSubmit = async () => {
    if (!hasAcceptedTerms) {
      return;
    }
    await submitForm();
  };
  
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };
  
  const calculateMonthlyPayment = (amount: number, duration: number): number => {
    const rate = 0.05;
    const monthlyRate = rate / 12;
    const payment = (amount * monthlyRate * Math.pow(1 + monthlyRate, duration)) / 
                   (Math.pow(1 + monthlyRate, duration) - 1);
    return Math.round(payment);
  };
  
  const amount = Number(formData.amount) || 0;
  const duration = Number(formData.duration) || 0;
  const monthlyPayment = duration > 0 ? calculateMonthlyPayment(amount, duration) : 0;
  
  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Récapitulatif de votre demande</CardTitle>
          <CardDescription>
            Vérifiez vos informations avant de soumettre votre demande de crédit
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Simulation */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center font-semibold text-neutral-900">
                <CreditCard className="w-5 h-5 mr-2 text-primary-600" />
                Votre projet
              </h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => goToStep('simulation')}
                className="text-xs"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-neutral-600">Type de projet :</span>
                <div className="font-semibold">
                  {projectTypeLabels[formData.project_type] || formData.project_type}
                </div>
              </div>
              <div>
                <span className="text-neutral-600">Montant :</span>
                <div className="font-semibold text-primary-600">
                  {amount.toLocaleString('fr-FR')} €
                </div>
              </div>
              <div>
                <span className="text-neutral-600">Durée :</span>
                <div className="font-semibold">{duration} mois</div>
              </div>
            </div>
            
            {monthlyPayment > 0 && (
              <div className="mt-3 p-3 bg-primary-50 rounded border border-primary-200">
                <div className="flex items-center justify-between">
                  <span className="text-primary-700">Mensualité estimée* :</span>
                  <span className="font-bold text-lg text-primary-800">
                    {monthlyPayment.toLocaleString('fr-FR')} € / mois
                  </span>
                </div>
                <p className="text-xs text-primary-600 mt-1">
                  * Estimation indicative, le taux sera déterminé selon votre profil
                </p>
              </div>
            )}
          </div>
          
          {/* Informations personnelles */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center font-semibold text-neutral-900">
                <User className="w-5 h-5 mr-2 text-primary-600" />
                Informations personnelles
              </h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => goToStep('personal_info')}
                className="text-xs"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-neutral-600">Nom complet :</span>
                <div className="font-semibold">
                  {titleLabels[formData.title]} {formData.firstName} {formData.lastName}
                </div>
              </div>
              <div>
                <span className="text-neutral-600">Email :</span>
                <div className="font-semibold">{formData.email}</div>
              </div>
              <div>
                <span className="text-neutral-600">Date de naissance :</span>
                <div className="font-semibold">{formatDate(formData.birthDate)}</div>
              </div>
            </div>
          </div>
          
          {/* Adresse */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center font-semibold text-neutral-900">
                <MapPin className="w-5 h-5 mr-2 text-primary-600" />
                Adresse de résidence
              </h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => goToStep('address')}
                className="text-xs"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            </div>
            
            <div className="text-sm">
              <div className="font-semibold">
                {formData.street}
              </div>
              <div>
                {formData.postalCode} {formData.city}
              </div>
              <div className="text-neutral-600">
                {countryLabels[formData.country]}
              </div>
            </div>
          </div>

          {/* Opt-In */}
          <div className="border rounded-lg p-4 bg-neutral-50">
            <h3 className="font-semibold text-neutral-900 mb-4">
              Préférences de communication
            </h3>

            {/* Opt-in Yelloan */}
            <div className="mb-4">
              <p className="text-sm text-neutral-700 mb-2">
                J'accepte de recevoir par e-mail les offres de Yelloan
              </p>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="yelloanOffers"
                    checked={acceptYelloanOffers === true}
                    onChange={() => { setAcceptYelloanOffers(true); updateField('acceptYelloanOffers', true); }}
                    className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">Oui</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="yelloanOffers"
                    checked={acceptYelloanOffers === false}
                    onChange={() => { setAcceptYelloanOffers(false); updateField('acceptYelloanOffers', false); }}
                    className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">Non</span>
                </label>
              </div>
            </div>

            {/* Opt-in Partenaires */}
            <div>
              <p className="text-sm text-neutral-700 mb-2">
                J'accepte de recevoir par e-mail les offres de nos partenaires
              </p>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="partnerOffers"
                    checked={acceptPartnerOffers === true}
                    onChange={() => { setAcceptPartnerOffers(true); updateField('acceptPartnerOffers', true); }}
                    className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">Oui</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="partnerOffers"
                    checked={acceptPartnerOffers === false}
                    onChange={() => { setAcceptPartnerOffers(false); updateField('acceptPartnerOffers', false); }}
                    className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">Non</span>
                </label>
              </div>
            </div>
          </div>

          {/* Acceptation des conditions */}
          <div className="border rounded-lg p-4 bg-neutral-50">
            <h3 className="font-semibold text-neutral-900 mb-3">
              Conditions générales
            </h3>
            
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasAcceptedTerms}
                onChange={(e) => { setHasAcceptedTerms(e.target.checked); updateField('hasAcceptedTerms', e.target.checked); }}
                className="mt-1 w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-700">
                J'accepte les{' '}
                <a href="#" className="text-primary-600 underline hover:text-primary-700">
                  conditions générales d'utilisation
                </a>{' '}
                et la{' '}
                <a href="#" className="text-primary-600 underline hover:text-primary-700">
                  politique de confidentialité
                </a>. 
                Je consens au traitement de mes données personnelles pour l'étude de ma demande de crédit.
              </span>
            </label>
          </div>
          
          
          {/* Informations importantes */}
          <Alert variant="info">
            <AlertDescription>
              <strong>Étapes suivantes :</strong>
              <br />
              1. Nous étudions votre dossier (réponse sous 24h)
              <br />
              2. Si votre demande est acceptée, vous recevrez les documents à signer
              <br />
              3. Les fonds seront versés sur votre compte après signature
            </AlertDescription>
          </Alert>
          
          <FormNavigation
            onPrevious={goToPreviousStep}
            onNext={handleSubmit}
            canGoBack={canGoBack}
            canGoNext={hasAcceptedTerms}
            isLoading={isSubmitting}
            isLastStep={true}
            nextButtonText="Soumettre ma demande de crédit"
          />
        </CardContent>
      </Card>
    </div>
  );
}